/**
 * useReceiveHistory — BLU-3xx Phase 47.01.2-B-05
 *
 * Wraps `inventory.listRecentReceipts` with the page-level inputs the Receive
 * History UI cares about: locationId (required), ownerOrgId (server-side
 * narrowing — avoids cross-page bleed when filtering to one client at a 3PL
 * with hundreds of receipts/day), fromDate (default last 7 days; widenable),
 * and cursor (cursor-based "Load more" pagination keyed on `created_at`).
 *
 * Cross-org behaviour:
 *   - Server resolves `accessibleOrgIds` for `locationId` and returns
 *     receipts owned by ANY accessible org by default.
 *   - When `ownerOrgId` is passed, the server validates it against the
 *     accessible-orgs set and FORBIDDEN's if not. Filtering at the server
 *     prevents short pages caused by client-side post-filter on a fixed
 *     `limit` window.
 *
 * Idempotency / consistency: the response excludes already-reversed receipts
 * (`undone_at IS NULL`) at the DAO level, so the page never has to grey-out
 * "ghost" rows.
 */
import { trpc } from '@/lib/trpc';

interface UseReceiveHistoryArgs {
  locationId: string;
  ownerOrgId?: string;
  /** ISO timestamp lower bound — the page passes `subDays(new Date(), N).toISOString()`. */
  fromDate?: string;
  /** Opaque cursor token from the previous page's `nextCursor` —
   *  encodes the compound `(createdAt, id)` pair so rows sharing an
   *  identical `created_at` are not skipped (BLU-3xx Phase 47.01.2-B-fix
   *  HIGH-1). Treat as opaque on the client. */
  cursor?: string;
  limit?: number;
  /** When false, the query is disabled (e.g. no location selected yet). */
  enabled?: boolean;
}

export function useReceiveHistory({
  locationId,
  ownerOrgId,
  fromDate,
  cursor,
  limit = 50,
  enabled = true,
}: UseReceiveHistoryArgs) {
  return trpc.inventory.listRecentReceipts.useQuery(
    {
      locationId,
      ownerOrgId,
      fromDate,
      cursor,
      limit,
    },
    {
      // Disable when we don't have a location yet — the schema requires a uuid
      // and would 400. The page renders an empty/intro state instead.
      enabled: enabled && !!locationId,
      // 30s stale window matches the cadence of receive flows; users who
      // refresh the page expect to see their just-completed receipt.
      staleTime: 30_000,
    },
  );
}
